import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { Button } from './button.js';
import { errorLaw, errorMessage } from '../../api/client.js';
import { Tooltip } from './tooltip.js';

interface EmptyProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}

/** Empty screens teach what to do next (UX-SPEC principle 9). */
export function EmptyState({ icon: Icon = Inbox, title, description, action, className, compact }: EmptyProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center rounded-lg border border-dashed bg-card/50 text-center', compact ? 'px-4 py-8' : 'px-6 py-14', className)}>
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {description && <div className="mt-1 max-w-md text-sm text-muted-foreground">{description}</div>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

/** A failed load is never shown as "nothing here" (UX-SPEC principle 10). */
export function ErrorState({ error, onRetry, title = "Couldn't load this", className }: { error: unknown; onRetry?: () => void; title?: string; className?: string }) {
  const law = errorLaw(error);
  return (
    <div role="alert" className={cn('flex flex-col items-center rounded-lg border border-danger/30 bg-danger-soft px-6 py-10 text-center', className)}>
      <AlertTriangle className="mb-2 h-6 w-6 text-danger" aria-hidden />
      <h3 className="text-[15px] font-semibold">{title}</h3>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        {errorMessage(error)}
        {law && <Tooltip content={`Selling rule ${law}`}><span className="ml-1 cursor-help underline decoration-dotted">why</span></Tooltip>}
      </p>
      {onRetry && <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Inline banner for a non-fatal problem inside a page. */
export function InlineAlert({ tone = 'warning', children, className }: { tone?: 'warning' | 'danger' | 'info' | 'success'; children: ReactNode; className?: string }) {
  const tones = {
    warning: 'border-warning/30 bg-warning-soft text-warning',
    danger: 'border-danger/30 bg-danger-soft text-danger',
    info: 'border-info/30 bg-info-soft text-info',
    success: 'border-success/30 bg-success-soft text-success',
  } as const;
  return <div role={tone === 'danger' ? 'alert' : 'status'} className={cn('rounded-md border px-3 py-2 text-sm', tones[tone], className)}>{children}</div>;
}

/** The fixed wording when an AI engine returned nothing. Never fake AI output. */
export function AiUnavailable({ className }: { className?: string }) {
  return <InlineAlert tone="info" className={className}>AI couldn't help this time — you can do it by hand.</InlineAlert>;
}
